import { useMemo } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import AuthRoute from "../Auth/AuthRoute";
import ForgotPassword from "../Auth/ForgotPassword";
import Login from "../Auth/Login";
import SupportedAuthProviders from "../Auth/MicrospecSupportedAuthProvider";
import Dashboard from "../Dashboard/Dashboard";
import { addUserSettings } from "../Dashboard/Drawer/CommonElements/UserSettings";
import getDashboardElement from "../Dashboard/ElementMapper";
import { OrganizationContextProvider } from "../Dashboard/OrganizationContext";
import getPagesElement from "../Pages/ElementsMapper";
import { validateConfiguration } from "../Security/ConfigValidator";
import { SecurityMonitor } from "../Security/XSSProtection";
import { useMicrospecThemeLanguageContext } from "../Themes";
import PageRenderer from "./PageRenderer";
import { getOrDefault, verifyNonNull } from "./Utils";

let globalConfig;
let elements;

const ApplicationRoutes = ({ config }) => {
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const memoizedElements = useMemo(() => {
        try {
            // Validate configuration before processing routes
            const validatedConfig = validateConfiguration(config);

            elements = []
            globalConfig = validatedConfig;
            handleApplication(validatedConfig, localizationProvider)
            handlePages(validatedConfig);
            handleAuth(validatedConfig)
            handleDashboards(validatedConfig);
            handle404();

            return elements
        } catch (error) {
            SecurityMonitor.reportViolation(
                'ROUTE_CONFIGURATION_ERROR',
                `Error processing route configuration: ${error.message}`,
                {
                    severity: 'CRITICAL',
                    error: error.toString(),
                    timestamp: new Date().toISOString()
                }
            );

            // Return minimal safe routes
            return [
                {
                    path: "*",
                    element: getPagesElement({ type: "NotFound" })
                }
            ];
        }
    }, [config, localizationProvider]);
    return useRoutes(memoizedElements)
};

const handleApplication = (config, localizationProvider) => {
    config.application = getOrDefault(config.application, {})
    const applicationName = getOrDefault(config?.application?.name, {
        type: "Literal",
        text: "Application"
    });
    const applicationNameText = localizationProvider.translateByType(applicationName)
    config.application.name = applicationNameText;

    const applicationSlogan = getOrDefault(config?.application?.slogan, {
        type: "Literal",
        text: "Your app slogan goes here."
    });
    config.application.slogan = localizationProvider.translateByType(applicationSlogan);
    document.title = applicationNameText;

    // Handle Auth Configuration
    config.application.auth = getOrDefault(config.application.auth, {})
    config.application.auth.enabledProviders = getOrDefault(config.application.auth.enabledProviders, [])
        .filter(provider => Object.values(SupportedAuthProviders).includes(provider))
    config.application.auth.verificationRequired = getOrDefault(config.application.auth.verificationRequired, false)
    config.application.auth.rolesAssigned = getOrDefault(config.application.auth.rolesAssigned, [])
}

const handlePages = (config) => {
    const pages = config.pages = getOrDefault(config.pages, [])
    for (let index = 0; index < pages.length; index++) {
        handleSinglePage(pages[index], config.pagesCommons)
    }
}

const handleSinglePage = (page, pagesCommons = {}) => {
    verifyPage(page, pagesCommons)
    elements.push({
        path: page.path,
        element: <PageRenderer
            prePageLoad={page.prePageLoad}
        >
                <OrganizationContextProvider organizationContextAware={page.organizationContextAware}>
                    {page.auth.enabled ? <AuthRoute
                        config={globalConfig}
                        path={page.path}
                        element={page.element}
                        neededRoles={page.auth.roles}
                        organizationContextAware={page.organizationContextAware}
                    /> : page.element}
                </OrganizationContextProvider>
        </PageRenderer>
    })
}

const verifyPage = (page, pagesCommons = {}) => {
    verifyString(page.path, "Path must be specified and of type string.")
    verifyAuth(page)
    verifyPageSpec(page, pagesCommons)
}

const verifyAuth = (obj) => {
    obj.auth = getOrDefault(obj.auth, {})
    obj.auth.enabled = getOrDefault(obj.auth.enabled, false)
    obj.auth.roles = getOrDefault(obj.auth.roles, [])
}

const verifyPageSpec = (obj, pagesCommons = {}) => {
    obj.spec = getOrDefault(obj.spec, {})
    obj.spec.type = getOrDefault(obj.spec.type, "Simple")
    obj.spec.object = getOrDefault(obj.spec.object, <h1>Start building an awesome groovy page.</h1>)
    obj.element = getPagesElement(obj.spec, pagesCommons)
}

const verifyString = (str, message) => {
    if (!str) {
        throw new Error(message)
    }
}

const handleAuth = (config) => {
    handleLogin(config)
    handleForgotPassword(config)
}

const handleLogin = (config) => {
    const enableRegister = getOrDefault(config?.application?.auth?.enableRegister, true);
    elements.push({
        path: "/login",
        element: <Login config={config} registerMode={false} enableRegister={enableRegister} />
    })

    // Add register route only if registration is enabled
    if (enableRegister) {
        elements.push({
            path: "/register",
            element: <Login
                config={config}
                registerMode={true}
                enableRegister={enableRegister}
            />
        })
    }
}

const handleForgotPassword = (config) => {
    elements.push({
        path: "/forgot",
        element: <ForgotPassword config={config} />
    })
}

const handleDashboards = (config) => {
    const application = config.application
    config.dashboards = getOrDefault(config.dashboards, []);
    const dashboards = config.dashboards = getOrDefault(config.dashboards, []);
    for (let index = 0; index < dashboards.length; index++) {
        const dashboard = dashboards[index];
        verifyNonNull(dashboard, "Dashboard");
        handleSingleDashboard(application, dashboard);
    }
}

const handleSingleDashboard = (application, dashboard) => {
    verifyDashboard(application, dashboard);
}

const verifyDashboard = (application, dashboard) => {
    verifyString(dashboard.path, "Path must be specified and of type string.");
    verifyRoles(dashboard);
    verifyDashboardItems(application, dashboard);
}

const handleDashboardItemSpec = (application, dashboard, items) => {
    const itemsLastIndex = items.length - 1
    items[itemsLastIndex].element = getDashboardElement(items[itemsLastIndex].spec)
    addItemPath(application, dashboard, items);
}

const handleDashboardItemSpecs = (application, dashboard, item) => {
    const specs = item.specs = getOrDefault(item.specs, []);
    for (const internalItem of specs) {
        handleDashboardItemSpec(application, dashboard, [item, internalItem]);
    }
}

const verifyRoles = (dashboard) => {
    dashboard.roles = getOrDefault(dashboard.roles, [])
}

const verifyDashboardItems = (application, dashboard) => {
    const items = dashboard.items = getOrDefault(dashboard.items, []);
    addCommonItems(dashboard.items);

    for (let index = 0; index < items.length; index++) {
        const item = items[index];
        verifyString(item.path, "Path must be specified and of type string.");
        verifyString(item.name, "Name must be specified and of type string.");
        verifyNonNull(item.icon, "Icon");
        const isSingleSpec = verifyItemSpecAndSpecsMutualExclusivity(item);
        isSingleSpec ? handleDashboardItemSpec(application, dashboard, [item]) : handleDashboardItemSpecs(application, dashboard, item);
    }
    handleDashboardBasePath(dashboard, items[0])
}

const verifyItemSpecAndSpecsMutualExclusivity = (item) => {
    if (item.spec && item.specs) {
        console.error("Item cannot have both spec and specs.");
        console.error("Item: ", item);
        throw new Error("Item cannot have both spec and specs.")
    }
    if (!item.spec && !item.specs) {
        console.error("Item must have either spec or specs.");
        console.error("Item: ", item);
        throw new Error("Item must have either spec or specs.")
    }
    if (item.spec) {
        return true;
    }
    if (item.specs) {
        return false;
    }
    // This should never be reached due to the validation above, but ensures all paths return a value
    return false;
}

const handleDashboardBasePath = (dashboard, item) => {
    const basePathElementSpec = dashboard.basePathElementSpec = getOrDefault(dashboard.basePathElementSpec, {})
    basePathElementSpec.path = dashboard.path

    const dashboardMainElement = {
        path: dashboard.path,
        spec: basePathElementSpec
    }
    if (basePathElementSpec.type && basePathElementSpec.object) {
        handleSinglePage(dashboardMainElement)
    } else if (item) {
        elements.push({
            path: dashboardMainElement.path,
            element: <Navigate to={item.fullPath} replace={true} />
        })
    }
}

const addItemPath = (application, dashboard, items) => {
    let itemFullPath = dashboard.path
    for (let item of items) {
        itemFullPath += "/" + item.path
    }
    const itemsLastIndex = items.length - 1
    items[itemsLastIndex].fullPath = itemFullPath;
    elements.push({
        path: itemFullPath,
        element:
            <PageRenderer prePageLoad={dashboard.prePageLoad}>
                <OrganizationContextProvider organizationContextAware={dashboard.organizationContextAware}>
                    <AuthRoute
                        config={globalConfig}
                        path={itemFullPath}
                        element={
                            <Dashboard
                                application={application}
                                dashboard={dashboard}
                                parent={items[0]}
                                item={items[itemsLastIndex]}
                                organizationContextAware={dashboard.organizationContextAware}
                                computeQuickAccessItems={dashboard.computeQuickAccessItems}
                            />
                        }
                        neededRoles={dashboard.roles}
                        organizationContextAware={dashboard.organizationContextAware}
                    />
                </OrganizationContextProvider>
            </PageRenderer>
    })
}

const handle404 = () => {
    elements.push({
        path: "*",
        element: getPagesElement({ type: "NotFound" }),
    })
}

const addCommonItems = (itemsList) => {
    addUserSettings(itemsList)
}


export default ApplicationRoutes;